import React, { PureComponent, Fragment } from 'react';
import 'url-search-params-polyfill';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import rulesCodes from '@config/rules-constants';
import styled from 'styled-components';
import moment from 'moment';
import { fieldTypes } from '@actions/conference';
import { CONFERENCE_STATUSES } from '@config/statuses-constants';
import { ParticipantsTable, People } from '@organisms';
import messages from './messages';
import { ChipsInput } from '@molecules';
import { H2, CheckboxBlock } from '@atoms';
import { FlexContainer, Whitespace, ConferenceCallInformation } from '@layout';

const HelperLabel = styled.span`
  display: block;
  font-family: Montserrat;
  font-size: 12px;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #808080;
`;
const DateLabel = styled.span`
   {
    display: block;
    font-family: Lato;
    font-size: 16px;
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #00819b;
    padding-top: 12px;
  }
`;

const InfoLabel = styled.span`
  display: block;
  font-size: 21px;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #4a4a4a;
`;

class ConferenceCallView extends PureComponent {
  static childContextTypes = {
    isConferenceDeleted: PropTypes.bool,
  };

  getChildContext = () => {
    const isConferenceDeleted =
      this.props.conferenceData.status === CONFERENCE_STATUSES.DELETED;
    return {
      isConferenceDeleted,
    };
  };

  applyOrganizationRules() {
    const { organization = null } = this.props.conferenceData;
    const resultRules = {
      isMFARequiredRule: false,
    };

    if (!organization) {
      return resultRules;
    }

    const { rules = [] } = organization;
    return {
      isMFARequiredRule: rules.some(
        ({ code }) => rulesCodes.MULTI_FACTOR_AUTHORIZATION === code,
      ),
      isTranscriptRequiredRule: rules.some(
        ({ code }) => rulesCodes.HUMAN_TRANSCRIPTION_REQUIRED === code,
      ),
      isAITranscriptRequiredRule: rules.some(
        ({ code }) => rulesCodes.AI_TRANSCRIPTION_REQUIRED === code,
      ),
      isDownloadCallRecordingsRule: rules.some(
        ({ code }) => rulesCodes.DOWNLOAD_CALL_RECORDINGS === code,
      ),
    };
  }

  handleUsingTwoFactorAuth = (e, value) => {
    this.props.setField(fieldTypes.USE_TWO_FACTOR_AUTH, value);
  };

  render() {
    const {
      conferenceData: {
        clientReference,
        date,
        isAITranscriptNeeded = false,
        isTranscriptNeeded = false,
        isTwoFactorAuthRequired = false,
        organization,
        tags,
        title,
        expandedId,
        status,
        people,
      },
      setField,
      isCurrentUserSuperAdmin,
      addPerson,
      setPersonField,
      removePerson,
      numbersByCountries,
      requestTranscriptCompletionEmail,
    } = this.props;
    const organizationRules = this.applyOrganizationRules();
    const {
      isTranscriptRequiredRule = false,
      isAITranscriptRequiredRule = false,
      isDownloadCallRecordingsRule = false,
    } = organizationRules;
    const isConferenceDeleted = status === CONFERENCE_STATUSES.DELETED;
    const { name: organizationName = '' } = organization || {};
    const attendees = people.map((attendee) => ({
      ...attendee,
      readOnly: !!attendee.organizationId,
    }));
    return (
      <div>
        <FlexContainer vertical gap="1.5rem">
          <ConferenceCallInformation.MainFieldsContainerFixed>
            <H2>
              <FormattedMessage {...messages.conferenceCallInformation} />
            </H2>

            <Whitespace size="1rem" />

            <HelperLabel>
              <FormattedMessage {...messages.conferenceDate} />
            </HelperLabel>
            <DateLabel>{moment(date).format('MM/DD/YYYY')}</DateLabel>
          </ConferenceCallInformation.MainFieldsContainerFixed>

          <FlexContainer>
            <ConferenceCallInformation.MainFieldsContainerFixed>
              <HelperLabel>
                <FormattedMessage {...messages.transcriptTitle} />
              </HelperLabel>
              <Whitespace size="8px" />
              <InfoLabel>{title}</InfoLabel>

              <Whitespace size="1.5rem" />

              <HelperLabel>
                <FormattedMessage {...messages.clientReference} />
              </HelperLabel>
              <Whitespace size="8px" />
              <InfoLabel>{clientReference}</InfoLabel>
            </ConferenceCallInformation.MainFieldsContainerFixed>

            <ConferenceCallInformation.MainFieldsContainerFixed>
              <HelperLabel>
                <FormattedMessage {...messages.organization} />
              </HelperLabel>
              <Whitespace size="8px" />
              <InfoLabel>{organizationName}</InfoLabel>

              <Whitespace size="0.8rem" />

              <ChipsInput
                id="tags"
                floatingLabelText={<FormattedMessage {...messages.tags} />}
                chips={tags}
                addChip={() => {}}
                deleteChip={() => {}}
                disabled={isConferenceDeleted}
              />
            </ConferenceCallInformation.MainFieldsContainerFixed>
          </FlexContainer>
        </FlexContainer>

        <Whitespace size="1rem" />

        {isCurrentUserSuperAdmin ? (
          <People
            isCurrentUserSuperAdmin={isCurrentUserSuperAdmin}
            addPerson={addPerson}
            setPersonField={setPersonField}
            removePerson={removePerson}
            people={attendees}
            conferenceHasBeenFinished
            header={<FormattedMessage {...messages.people} />}
            numbersByCountries={numbersByCountries}
            requestTranscriptCompletionEmail={requestTranscriptCompletionEmail}
          />
        ) : (
          <div>
            <H2>
              <FormattedMessage {...messages.people} />
            </H2>
            <ParticipantsTable
              people={people}
              setField={setField}
              expandedId={expandedId}
              isConferenceDeleted={isConferenceDeleted}
            />
          </div>
        )}

        <Whitespace size="1.5rem" />

        <ConferenceCallInformation.MainFieldsContainerFixed />

        <Whitespace size="1rem" />

        <Fragment>
          <Whitespace size="1rem" />
          <CheckboxBlock
            disabled={isConferenceDeleted}
            id="request-two-factor-auth"
            label={<FormattedMessage {...messages.useTwoFactorAuth} />}
            checked={isTwoFactorAuthRequired}
            onCheck={this.handleUsingTwoFactorAuth}
          />
          <Whitespace size="1rem" />
          <CheckboxBlock
            disabled
            id="create-transcription"
            label={<FormattedMessage {...messages.createHumanTranscription} />}
            checked={isTranscriptRequiredRule || isTranscriptNeeded}
            onCheck={() => {}}
          />
          <Whitespace size="1rem" />
          <CheckboxBlock
            disabled
            id="create-ai-transcription"
            label={<FormattedMessage {...messages.createAITranscription} />}
            checked={isAITranscriptRequiredRule || isAITranscriptNeeded}
            onCheck={() => {}}
          />
          <Whitespace size="1rem" />
          <CheckboxBlock
            disabled
            id="allow-audiofile-download-rule"
            label={<FormattedMessage {...messages.allowAudioDownload} />}
            checked={isDownloadCallRecordingsRule}
          />
          <Whitespace size="1rem" />
        </Fragment>
      </div>
    );
  }
}

ConferenceCallView.propTypes = {
  conferenceData: PropTypes.object,
};

ConferenceCallView.defaultProps = {};

export default ConferenceCallView;



// WEBPACK FOOTER //
// ./components/templates/ConferenceCallView/ConferenceCallView.js